import type { Timestamp, QueryDocumentSnapshot, Query, DocumentData, QuerySnapshot } from 'firebase/firestore';
import { db, CallFoundryDexie } from '../db';
import { isLocalOnlyUser, isLocalLearner, getActiveUserId } from '../identity/learnerIdentity';
import type { CanonicalTrainingEvent, TrainingAttempt, Pillar } from '../models';
import { diagnosticJournal } from '../../services/diagnosticJournal';
import { COMPETENCY_METADATA } from '../events/eventService';
import {
  recomputeCompetencyProgress,
  recomputeLearnerProgress,
} from '../progress/progressEngine';
import { classifySyncError } from './syncErrors';
import { assertNoRawAudioPayload } from './audioSyncRejection';

const MAX_SYNC_ATTEMPTS = 5;

export interface SyncStatus {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export class SyncQueueService {
  private dexie: CallFoundryDexie;
  private isSyncing = false;
  private lastSyncedAt: string | null = null;
  private lastError: string | null = null;
  private listeners: Array<(status: SyncStatus) => void> = [];

  constructor(databaseInstance: CallFoundryDexie = db) {
    this.dexie = databaseInstance;
  }

  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.push(listener);
    listener(this.getStatus());
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify(): void {
    const status = this.getStatus();
    for (const listener of this.listeners) {
      try {
        listener(status);
      } catch {
        // ignore
      }
    }
  }

  getStatus(): SyncStatus {
    return {
      isSyncing: this.isSyncing,
      pendingCount: 0,
      lastSyncedAt: this.lastSyncedAt,
      lastError: this.lastError,
    };
  }

  async getPendingCount(): Promise<number> {
    try {
      return await this.dexie.syncQueue
        .where('status')
        .equals('pending')
        .count();
    } catch {
      return 0;
    }
  }

  /**
   * Pushes pending queue items to Cloud Firestore replica.
   *
   * Architectural Invariants Enforced:
   * 1. Race-Safe Idempotency: Uses Firestore runTransaction to prevent TOCTOU race conditions.
   * 2. Existing Remote Event Verification: Verifies immutable identity fields (eventId, originLearnerId,
   *    deviceId, deviceSequence, eventType, payload). Detects REMOTE_IMMUTABLE_CONFLICT on mismatch.
   * 3. Server Timestamp Truthfulness: Distinguishes localAckAt from normalized Firestore serverReceivedAt.
   * 4. Canonical Immutability: Zero mutation to local CanonicalTrainingEvent; transport state isolated in syncMetadata.
   * 5. Lazy Firebase Loading: Dynamically imports Firebase Firestore on demand to protect local learners.
   */
  async processSyncQueue(): Promise<{
    processed: number;
    failed: number;
  }> {
    if (this.isSyncing) return { processed: 0, failed: 0 };
    if (isLocalOnlyUser()) return { processed: 0, failed: 0 };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { processed: 0, failed: 0 };
    }

    this.isSyncing = true;
    this.notify();

    let processed = 0;
    let failed = 0;

    try {
      // Lazy load Firestore SDK on demand
      const { doc, runTransaction, serverTimestamp } = await import('firebase/firestore');
      const { getFirebaseFirestore } = await import('../../infrastructure/firebase/firebaseClient');

      const firestore = getFirebaseFirestore();
      const currentUid = getActiveUserId();

      const pendingItems = await this.dexie.syncQueue
        .where('status')
        .equals('pending')
        .limit(25) // Spark free-tier batch chunk
        .toArray();

      const itemsToSync = pendingItems.filter(
        (item) => !item.accountUid || item.accountUid === currentUid
      );

      for (const item of itemsToSync) {
        if (item.attempts >= MAX_SYNC_ATTEMPTS) {
          await this.dexie.syncQueue.update(item.id, { status: 'failed' });
          failed++;
          continue;
        }

        const event = await this.dexie.events.get(item.eventId);
        if (!event) {
          // Event no longer exists locally; clean up queue item
          await this.dexie.syncQueue.delete(item.id);
          continue;
        }

        try {
          // Audio privacy boundary guard (Directive 8)
          assertNoRawAudioPayload(event.payload);

          const docRef = doc(firestore, 'users', currentUid, 'events', event.eventId);
          const localAckTime = new Date().toISOString();
          let serverTimeIso: string | undefined;

          // RACE-SAFE FIRESTORE TRANSACTION:
          // Resolves concurrency between workers, retries, and duplicate requests
          await runTransaction(firestore, async (transaction) => {
            const eventSnap = await transaction.get(docRef);

            if (!eventSnap.exists()) {
              // Absent: Write remote immutable event with server timestamp
              const cleanEvent = JSON.parse(JSON.stringify(event));
              transaction.set(docRef, {
                ...cleanEvent,
                userId: currentUid,
                serverReceivedAt: serverTimestamp(),
              });
            } else {
              // Exists: Compare immutable identity fields to ensure identical canonical event
              const remoteData = eventSnap.data();
              const isMatch =
                remoteData.eventId === event.eventId &&
                remoteData.originLearnerId === event.originLearnerId &&
                remoteData.deviceId === event.deviceId &&
                remoteData.deviceSequence === event.deviceSequence &&
                remoteData.eventType === event.eventType &&
                JSON.stringify(remoteData.payload ?? {}) === JSON.stringify(event.payload ?? {});

              if (!isMatch) {
                throw new Error(
                  `REMOTE_IMMUTABLE_CONFLICT: Remote event ${event.eventId} contains conflicting immutable data.`
                );
              }

              // Reconcile normalized server timestamp from existing document
              if (remoteData.serverReceivedAt) {
                const sAt = remoteData.serverReceivedAt as Timestamp | string;
                if (typeof (sAt as Timestamp).toDate === 'function') {
                  serverTimeIso = (sAt as Timestamp).toDate().toISOString();
                } else if (typeof sAt === 'string') {
                  serverTimeIso = sAt;
                }
              }
            }
          });

          // Commit sync status to Dexie transport metadata (leaving canonical event untouched)
          await this.dexie.transaction('rw', [this.dexie.syncMetadata, this.dexie.syncQueue], async () => {
            await this.dexie.syncMetadata.put({
              eventId: event.eventId,
              status: 'synced',
              localAckAt: localAckTime,
              serverReceivedAt: serverTimeIso ?? localAckTime,
              remoteDocPath: docRef.path,
              accountUid: currentUid,
            });
            await this.dexie.syncQueue.delete(item.id);
          });

          processed++;
        } catch (err: unknown) {
          const errorDetails = classifySyncError(err);
          const nextAttempts = item.attempts + 1;
          const nextStatus =
            !errorDetails.isRetryable || nextAttempts >= MAX_SYNC_ATTEMPTS
              ? 'failed'
              : 'pending';

          await this.dexie.syncQueue.update(item.id, {
            attempts: nextAttempts,
            lastAttemptAt: new Date().toISOString(),
            status: nextStatus,
            errorCategory: errorDetails.category,
            error: errorDetails.message,
          });

          await this.dexie.syncMetadata.put({
            eventId: event.eventId,
            status: 'failed',
            accountUid: currentUid,
            errorCategory: errorDetails.category,
            errorMessage: errorDetails.message,
          });

          await diagnosticJournal.warn('SyncQueueService', `Failed syncing event ${event.eventId}`, {
            category: errorDetails.category,
            error: errorDetails.message,
            isRetryable: errorDetails.isRetryable,
          });

          failed++;
        }
      }

      this.lastSyncedAt = new Date().toISOString();
      this.lastError = null;
    } catch (err: unknown) {
      const errorDetails = classifySyncError(err);
      this.lastError = errorDetails.message;
      await diagnosticJournal.error('SyncQueueService', 'Sync queue batch processing error', {
        category: errorDetails.category,
        error: errorDetails.message,
      });
    } finally {
      this.isSyncing = false;
      this.notify();
    }

    return { processed, failed };
  }

  /**
   * Pulls remote events from Firestore replica and merges into local Dexie.
   *
   * Features:
   * 1. Server timestamp normalization: converts Firestore Timestamp objects to ISO 8601 strings.
   * 2. Idempotent pull: pulling multiple times produces identical state without duplicating events.
   * 3. Tombstone preservation: deletes local attempts so tombstoned state does not resurrect.
   * 4. State reconstruction: recalculates minimal M2 derived progress from canonical events.
   */
  async pullRemoteEvents(userId: string): Promise<{
    pulledCount: number;
    newEventsCount: number;
  }> {
    if (!userId || isLocalLearner(userId)) return { pulledCount: 0, newEventsCount: 0 };

    try {
      const { collection, getDocs, query, orderBy, limit, startAfter } =
        await import('firebase/firestore');
      const { getFirebaseFirestore } = await import('../../infrastructure/firebase/firebaseClient');

      const firestore = getFirebaseFirestore();
      const eventsRef = collection(firestore, 'users', userId, 'events');

      const PAGE_SIZE = 100;
      let lastDoc: QueryDocumentSnapshot | null = null;
      let hasMore = true;
      let totalPulled = 0;
      let newEventsCount = 0;

      while (hasMore) {
        const q: Query<DocumentData> = lastDoc
          ? query(eventsRef, orderBy('clientCreatedAt', 'asc'), startAfter(lastDoc), limit(PAGE_SIZE))
          : query(eventsRef, orderBy('clientCreatedAt', 'asc'), limit(PAGE_SIZE));

        const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
        if (snapshot.empty) {
          hasMore = false;
          break;
        }

        totalPulled += snapshot.docs.length;
        lastDoc = snapshot.docs[snapshot.docs.length - 1];

        await this.dexie.transaction(
          'rw',
          [
            this.dexie.events,
            this.dexie.attempts,
            this.dexie.masteryProfiles,
            this.dexie.learnerProfiles,
            this.dexie.syncMetadata,
          ],
          async () => {
            for (const docSnap of snapshot.docs) {
              const rawData = docSnap.data();

              // Normalize serverReceivedAt
              let normalizedServerReceivedAt: string | undefined;
              if (rawData.serverReceivedAt) {
                const sAt = rawData.serverReceivedAt as Timestamp | string;
                if (typeof (sAt as Timestamp).toDate === 'function') {
                  normalizedServerReceivedAt = (sAt as Timestamp).toDate().toISOString();
                } else if (typeof sAt === 'string') {
                  normalizedServerReceivedAt = sAt;
                }
              }

              const existing = await this.dexie.events.get(rawData.eventId);

              if (!existing) {
                const cleanEvent: CanonicalTrainingEvent = {
                  eventId: rawData.eventId,
                  originLearnerId: rawData.originLearnerId || 'loc_unknown',
                  deviceId: rawData.deviceId,
                  deviceSequence: typeof rawData.deviceSequence === 'number' ? rawData.deviceSequence : 0,
                  eventType: rawData.eventType,
                  skillId: rawData.skillId,
                  scenarioId: rawData.scenarioId,
                  difficulty: typeof rawData.difficulty === 'number' ? rawData.difficulty : 1.0,
                  independenceMode: rawData.independenceMode || 'practice',
                  scoringVersion: rawData.scoringVersion || '1.0.0',
                  contentVersion: rawData.contentVersion || '1.0.0',
                  payloadVersion: rawData.payloadVersion || '1.0.0',
                  clientCreatedAt: rawData.clientCreatedAt,
                  payload: rawData.payload ?? {},
                };

                await this.dexie.events.put(cleanEvent);
                await this.dexie.syncMetadata.put({
                  eventId: cleanEvent.eventId,
                  status: 'synced',
                  localAckAt: new Date().toISOString(),
                  serverReceivedAt: normalizedServerReceivedAt ?? new Date().toISOString(),
                  remoteDocPath: docSnap.ref.path,
                  accountUid: userId,
                });

                newEventsCount++;

                // Process TOMBSTONE events
                if (cleanEvent.eventType === 'TOMBSTONE_DELETED' && cleanEvent.payload) {
                  const targetType = cleanEvent.payload.targetType as string;
                  const targetId = cleanEvent.payload.targetId as string;
                  if (targetType === 'attempt' && targetId) {
                    await this.dexie.attempts.delete(targetId);
                  }
                }

                // Process ATTEMPT_COMPLETED events
                if (
                  cleanEvent.eventType === 'ATTEMPT_COMPLETED' &&
                  cleanEvent.skillId &&
                  cleanEvent.payload
                ) {
                  const payload = cleanEvent.payload as Record<string, unknown>;
                  const attemptId = (payload.attemptId as string) || cleanEvent.eventId;
                  const existingAttempt = await this.dexie.attempts.get(attemptId);
                  if (!existingAttempt) {
                    const attempt: TrainingAttempt = {
                      id: attemptId,
                      drillId: (payload.drillId as string) || 'drill-unknown',
                      skillId: cleanEvent.skillId,
                      score: typeof payload.score === 'number' ? payload.score : 80,
                      evidenceConfidence: 0.2,
                      independenceMode: cleanEvent.independenceMode || 'practice',
                      difficulty: cleanEvent.difficulty || 1.0,
                      timeSpentMs:
                        typeof payload.timeSpentMs === 'number' ? payload.timeSpentMs : 60000,
                      userResponses: {},
                      feedback: '',
                      completedAt: cleanEvent.clientCreatedAt,
                      synced: true,
                    };
                    await this.dexie.attempts.put(attempt);
                  }
                }
              } else {
                // Document already in local Dexie; ensure syncMetadata reflects synced status
                const meta = await this.dexie.syncMetadata.get(rawData.eventId);
                if (!meta || meta.status !== 'synced') {
                  await this.dexie.syncMetadata.put({
                    eventId: rawData.eventId,
                    status: 'synced',
                    localAckAt: new Date().toISOString(),
                    serverReceivedAt: normalizedServerReceivedAt ?? new Date().toISOString(),
                    remoteDocPath: docSnap.ref.path,
                    accountUid: userId,
                  });
                }
              }
            }
          }
        );

        if (snapshot.docs.length < PAGE_SIZE) {
          hasMore = false;
          break;
        }
      }

      // Recompute derived state once after all pages are pulled
      await this.dexie.transaction(
        'rw',
        [
          this.dexie.attempts,
          this.dexie.masteryProfiles,
          this.dexie.learnerProfiles,
        ],
        async () => {
          const allAttempts = await this.dexie.attempts.toArray();
          const skillGroups = new Map<string, typeof allAttempts>();
          for (const a of allAttempts) {
            const list = skillGroups.get(a.skillId) || [];
            list.push(a);
            skillGroups.set(a.skillId, list);
          }

          for (const [skillId, attempts] of skillGroups.entries()) {
            const meta = COMPETENCY_METADATA[skillId] ?? {
              name: skillId,
              pillar: 'communication' as Pillar,
            };
            const competencyMastery = recomputeCompetencyProgress(
              skillId,
              meta.name,
              meta.pillar,
              attempts
            );
            await this.dexie.masteryProfiles.put(competencyMastery);
          }

          const allCompetencies = await this.dexie.masteryProfiles.toArray();
          const existingProfile = await this.dexie.learnerProfiles.get(userId);
          const updatedProfile = recomputeLearnerProgress(
            userId,
            allCompetencies,
            existingProfile?.displayName ?? 'Agent 402',
            existingProfile?.activeJobProfile ?? 'inbound_telecom_care'
          );
          await this.dexie.learnerProfiles.put(updatedProfile);
        }
      );

      await diagnosticJournal.info('SyncQueueService', 'Remote events pulled successfully', {
        pulled: totalPulled,
        newEvents: newEventsCount,
      });

      return {
        pulledCount: totalPulled,
        newEventsCount,
      };
    } catch (err) {
      await diagnosticJournal.warn('SyncQueueService', 'Failed to pull remote events', {
        error: String(err),
      });
      return { pulledCount: 0, newEventsCount: 0 };
    }
  }
}

export const syncQueueService = new SyncQueueService();
